import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { dueLabel, taskTypeLabel } from '../format.ts'

interface Task {
  id: string
  kind: string
  title: string
  dueAt: string | null
  customerId: string | null
  customerName: string | null
  assigneeCode: string | null
}
interface FollowUp {
  id: string
  title: string
  dueAt: string | null
  quoteId: string | null
  customerName: string | null
}
interface Ticket {
  id: string
  number: string
  customerName: string
  serialNumber: string | null
  model: string | null
  machineDescription: string | null
  status: string
  priority: string
  warrantyActive: boolean
}
interface Dash {
  myTasks: Task[]
  followUps: FollowUp[]
  counts: { openCustomerOrders: number; awaitingSupplier: number }
  awaitingSupplier: { id: string; number: string }[]
  openTickets: Ticket[]
}

/** The home screen (SPEC 5.7): what needs me today, in one glance. */
export function DashboardPage() {
  const { user } = useAuth()
  const [d, setD] = useState<Dash | null>(null)

  function reload() {
    api.get<Dash>('/api/dashboard').then(setD).catch(() => {})
  }
  useEffect(reload, [])

  async function complete(id: string) {
    await api.post(`/api/tasks/${id}/complete`).catch(() => {})
    reload()
  }

  if (!user) return null

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-xl font-bold text-slate-800">Dzień dobry, {user.firstName}.</h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-5">Oto co czeka dziś na Ciebie.</p>

      {!d ? (
        <div className="text-slate-400 text-sm">Ładowanie…</div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {/* My tasks today */}
          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="font-semibold text-slate-700 text-sm mb-2">Zadania na dziś</h2>
            {d.myTasks.length === 0 ? (
              <p className="text-sm text-slate-400">Nic pilnego. Czysto.</p>
            ) : (
              <ul className="space-y-1.5">
                {d.myTasks.map((t) => {
                  const due = t.dueAt ? dueLabel(t.dueAt) : null
                  return (
                    <li key={t.id} className="flex items-start gap-2 text-sm">
                      <button
                        onClick={() => void complete(t.id)}
                        title="Oznacz jako zrobione"
                        className="mt-0.5 w-4 h-4 rounded border border-slate-300 hover:bg-green-100 hover:border-green-500 shrink-0"
                      />
                      <span className="min-w-0">
                        <span className="text-slate-700">{t.title}</span>
                        <span className="block text-xs text-slate-400">
                          {taskTypeLabel(t.kind)}
                          {t.customerName && ` · ${t.customerName}`}
                          {due && <span className={due.overdue ? 'text-red-600' : due.today ? 'text-amber-600' : ''}> · {due.label}</span>}
                          {!t.assigneeCode && ' · nieprzypisane'}
                        </span>
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
            <Link to="/zadania" className="text-xs text-brand-600 hover:underline mt-3 inline-block">
              Wszystkie zadania →
            </Link>
          </section>

          {/* Offers to follow up */}
          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="font-semibold text-slate-700 text-sm mb-2">Oferty do follow-upu</h2>
            {d.followUps.length === 0 ? (
              <p className="text-sm text-slate-400">Brak ofert czekających na kontakt.</p>
            ) : (
              <ul className="space-y-1.5">
                {d.followUps.map((f) => {
                  const due = f.dueAt ? dueLabel(f.dueAt) : null
                  return (
                    <li key={f.id} className="text-sm">
                      {f.quoteId ? (
                        <Link to={`/oferty/${f.quoteId}`} className="text-brand-700 hover:underline">
                          {f.title}
                        </Link>
                      ) : (
                        <span className="text-slate-700">{f.title}</span>
                      )}
                      <span className="block text-xs text-slate-400">
                        {f.customerName}
                        {due && <span className={due.overdue ? 'text-red-600' : ''}> · {due.label}</span>}
                      </span>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>

          {/* Orders in flight */}
          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="font-semibold text-slate-700 text-sm mb-2">Zamówienia w toku</h2>
            <div className="text-sm space-y-1">
              <Link to="/zamowienia" className="flex justify-between hover:bg-slate-50 rounded px-1 -mx-1">
                <span className="text-slate-600">Zamówienia klientów w realizacji</span>
                <span className="font-semibold text-slate-800">{d.counts.openCustomerOrders}</span>
              </Link>
              <div className="flex justify-between px-1">
                <span className="text-slate-600">Wysłane do producenta, bez potwierdzenia</span>
                <span className="font-semibold text-slate-800">{d.counts.awaitingSupplier}</span>
              </div>
            </div>
          </section>

          {/* Open service tickets */}
          <section className="bg-white border border-slate-200 rounded-lg p-4">
            <h2 className="font-semibold text-slate-700 text-sm mb-2">Serwis — otwarte zgłoszenia</h2>
            {d.openTickets.length === 0 ? (
              <p className="text-sm text-slate-400">Brak otwartych zgłoszeń.</p>
            ) : (
              <ul className="space-y-1.5">
                {d.openTickets.slice(0, 6).map((t) => (
                  <li key={t.id} className="text-sm">
                    <Link to={`/serwis/${t.id}`} className="text-brand-700 hover:underline">
                      {t.number}
                    </Link>
                    <span className="text-slate-500"> · {t.customerName}</span>
                    <span className="block text-xs text-slate-400">
                      {t.model ?? t.machineDescription}
                      {t.priority === 'wysoki' && <span className="text-red-600"> · pilne</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Link to="/serwis" className="text-xs text-brand-600 hover:underline mt-3 inline-block">
              Wszystkie zgłoszenia →
            </Link>
          </section>
        </div>
      )}
    </div>
  )
}
